import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Flex, Text, SelectField, Banner, spaces } from '@electricjs/arc';

import { CURRENT_CATALOG_SHIPPING_METHODS } from '@/constants/storefront';
import formatDate from '@/helpers/formatDate';
import { CatalogShippingMethodOption } from '@/types/storefront';

import { useStorefront } from '../../StorefrontProvider';
import { getCatalogShippingMethodDisplayName } from '../../utils/formatters';
import { useCurrentCatalogEstimatedDates } from '../../utils/useCurrentCatalogEstimatedDates';

const StyledPolicyList = styled.ul`
  padding-left: ${spaces.space_l};
  margin: 0;
`;

// establishes the containment context: the section's own available width (not the viewport's) is what the layout below reacts to
const ContainerContext = styled(Flex)`
  container-type: inline-size;
`;

// below this width, there isn't enough room for the shipping method and its policy side-by-side, so the policy moves underneath instead
const STACK_CONTAINER_WIDTH = '60rem';

const MethodAndPolicyRow = styled(Flex)`
  width: 100%;
  column-gap: ${spaces.space_3xl_rem};

  @container (max-width: ${STACK_CONTAINER_WIDTH}) {
    flex-direction: column;
    row-gap: ${spaces.space_m_rem};

    /* resets the children's 50% flex-basis (meant for the side-by-side row) so they take full width instead of half height when stacked */
    > * {
      flex: 1 1 auto;
      width: 100%;
    }
  }
`;

const CatalogShippingMethodSelector = () => {
  const { t } = useTranslation('storefront');

  const { storefrontBackorderPossibility } = useFlags();

  const {
    storefrontCheckoutForm,
    cartRequiresCatalogShippingMethod,
    cartRequiresWarehouseShippingMethod,
    canConfigureWinZtp,
  } = useStorefront();

  const estimatedDates = useCurrentCatalogEstimatedDates();

  const windowsZtpEnabled = storefrontCheckoutForm?.watch('windowsZtpEnabled');
  const wAutopilotEnabled = storefrontCheckoutForm?.watch('wAutopilotEnabled');

  const shoppingCountry = storefrontCheckoutForm?.watch('shoppingCountry');

  // Populating shipping options
  const shippingOptions: CatalogShippingMethodOption[] | undefined =
    useMemo(() => {
      // Just for typescript, because this component will never be rendered in a use-case that the shopping country doesn't exist or is "other"
      if (!shoppingCountry || shoppingCountry.value === 'other') {
        return undefined;
      }

      return CURRENT_CATALOG_SHIPPING_METHODS[shoppingCountry.value]?.map(
        option => {
          return {
            label: getCatalogShippingMethodDisplayName(option.method),
            value: option.method,
          };
        }
      );
    }, [shoppingCountry]);

  useEffect(() => {
    // Automatically selects the first option when there is no catalog shipping method available
    if (
      storefrontCheckoutForm?.getValues('catalogShippingMethod') ===
        undefined &&
      shippingOptions
    ) {
      storefrontCheckoutForm?.setValue(
        'catalogShippingMethod',
        shippingOptions[0]
      );
    }
  }, [shippingOptions, storefrontCheckoutForm]);

  if (!cartRequiresCatalogShippingMethod) {
    return null;
  }

  return (
    <ContainerContext vertical rowGap={spaces.space_m_rem} width="100%">
      <Text variant="heading-3">
        {cartRequiresWarehouseShippingMethod
          ? t('sections.shipping.method.titlePurchases')
          : t('sections.shipping.method.title')}
      </Text>

      {canConfigureWinZtp && windowsZtpEnabled && (
        <Banner
          id="delivery-estimates-ztp-banner"
          isFlat
          isFullWidth
          intent="warning"
          message={t('ztp.deliveryEstimatesBanner')}
        />
      )}

      {wAutopilotEnabled && (
        <Banner
          id="delivery-estimates-wautopilot-banner"
          isFlat
          isFullWidth
          intent="warning"
          message={t('wautopilot.deliveryEstimatesBanner')}
        />
      )}

      {storefrontBackorderPossibility && (
        <Banner
          id="storefront-backorder-possibility-banner"
          isFlat
          isFullWidth
          intent="error"
          title={t('backorder.banner.title')}
          message={t('backorder.banner.message')}
        />
      )}

      <MethodAndPolicyRow>
        {/* Shipping Method Selection */}
        <Flex vertical flex="0 0 50%">
          {storefrontCheckoutForm && (
            <SelectField
              id="shipping-method-selection"
              key={
                storefrontCheckoutForm?.getValues('catalogShippingMethod')
                  ?.value
              }
              name="catalogShippingMethod"
              label={t('sections.shipping.method.label')}
              control={storefrontCheckoutForm?.control}
              value={storefrontCheckoutForm?.getValues('catalogShippingMethod')}
              options={shippingOptions}
              required
            />
          )}
          {!storefrontBackorderPossibility && (
            <Flex vertical>
              <Text variant="label-large">
                {t('sections.shipping.method.estimatedShipDate')}{' '}
                {formatDate(estimatedDates?.shipDate?.toISOString())}
              </Text>
              <Text variant="label-large">
                {t('sections.shipping.method.estimatedDelivery')}{' '}
                {formatDate(estimatedDates?.deliveryDate?.toISOString())}
              </Text>
            </Flex>
          )}
        </Flex>

        {/* Shipping Method Policy */}
        <Flex vertical flex={`0 0 calc(50% - ${spaces.space_3xl_rem})`}>
          <Text>{t('sections.shipping.method.policyTitle')}</Text>
          <StyledPolicyList>
            <li>
              <Text>{t('sections.shipping.method.policyInsurance')}</Text>
            </li>
            <li>
              <Text>{t('sections.shipping.method.policySignature')} </Text>
            </li>
            <li>
              <Text>{t('sections.shipping.method.policyNoCancellation')}</Text>
            </li>
          </StyledPolicyList>
        </Flex>
      </MethodAndPolicyRow>
    </ContainerContext>
  );
};

export default CatalogShippingMethodSelector;
